import { memo, useMemo } from "react";
import { format, parseISO, addDays } from "date-fns";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { TimesheetCell } from "./TimesheetCell";
import { formatDuration } from "@/lib/time-utils";
import type { TimeEntry } from "@/types/time";

interface TimesheetMemberRowProps {
  name: string;
  email: string;
  entries: TimeEntry[];
  weekStart: string;
  projectColors: Map<string, string>;
  onClick: () => void;
}

export const TimesheetMemberRow = memo(function TimesheetMemberRow({
  name,
  email,
  entries,
  weekStart,
  projectColors,
  onClick,
}: TimesheetMemberRowProps) {
  const days = useMemo(() => {
    const monday = parseISO(weekStart);
    return Array.from({ length: 7 }, (_, i) => format(addDays(monday, i), "yyyy-MM-dd"));
  }, [weekStart]);

  const entriesByDay = useMemo(() => {
    const map = new Map<string, TimeEntry[]>();
    for (const day of days) {
      map.set(day, []);
    }
    for (const entry of entries) {
      const day = format(parseISO(entry.start), "yyyy-MM-dd");
      const existing = map.get(day);
      if (existing) existing.push(entry);
    }
    return map;
  }, [entries, days]);

  const weekTotal = useMemo(
    () => entries.reduce((sum, e) => sum + (e.durationSeconds ?? 0), 0),
    [entries]
  );

  const initials = name
    ? name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : email?.[0]?.toUpperCase() ?? "?";

  return (
    <div
      className="grid cursor-pointer items-center gap-2 rounded-md px-2 py-2 hover:bg-muted/50"
      style={{ gridTemplateColumns: "180px repeat(7, 1fr) 60px" }}
      onClick={onClick}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Avatar className="h-6 w-6 shrink-0">
          <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
        </Avatar>
        <span className="text-sm truncate">{name || email}</span>
      </div>

      {days.map((day) => (
        <TimesheetCell
          key={day}
          entries={entriesByDay.get(day) ?? []}
          date={day}
          projectColors={projectColors}
        />
      ))}

      <div className="text-right text-xs font-medium whitespace-nowrap">
        {formatDuration(weekTotal)}
      </div>
    </div>
  );
});
